import React, { useState, useEffect } from 'react';
import { Banner, Text, Stack, ProgressBar, Button } from '@shopify/polaris';
import { SystemSettingsService } from '../services/systemSettingsService';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Timestamp } from 'firebase/firestore';

const MaintenanceBanner = () => {
  const [maintenanceInfo, setMaintenanceInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(null);
  const { user, isAdmin } = useAuth();
  const [bypassMaintenance, setBypassMaintenance] = useState(false);
  
  useEffect(() => {
    // Check if admin has chosen to bypass maintenance
    const bypass = localStorage.getItem('bypassMaintenance') === 'true';
    setBypassMaintenance(bypass);
    
    const fetchMaintenanceInfo = async () => {
      try {
        const info = await SystemSettingsService.getMaintenanceInfo();
        console.log('Maintenance info fetched:', info);
        setMaintenanceInfo(info);
      } catch (error) {
        console.error('Error fetching maintenance info:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMaintenanceInfo();
    
    // Refresh every minute
    const interval = setInterval(fetchMaintenanceInfo, 60000);
    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    if (!maintenanceInfo?.scheduledMaintenanceDate) return;
    
    const calculateCountdown = () => {
      const now = new Date();
      // Handle both Timestamp objects and serialized timestamps
      let maintenanceDate;
      if (maintenanceInfo.scheduledMaintenanceDate instanceof Timestamp) {
        maintenanceDate = maintenanceInfo.scheduledMaintenanceDate.toDate();
      } else if (maintenanceInfo.scheduledMaintenanceDate?.seconds) {
        // Handle serialized Timestamp objects
        maintenanceDate = new Date(maintenanceInfo.scheduledMaintenanceDate.seconds * 1000);
      } else {
        // Fallback for string dates
        maintenanceDate = new Date(maintenanceInfo.scheduledMaintenanceDate);
      }
      
      if (now >= maintenanceDate) {
        setCountdown(null);
        return;
      }
      
      const diffMs = maintenanceDate - now;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      setCountdown({
        days: diffDays,
        hours: diffHours,
        minutes: diffMinutes,
        totalMs: diffMs,
        percentage: Math.min(100, Math.max(0, (diffMs / (24 * 60 * 60 * 1000)) * 100))
      });
    };
    
    calculateCountdown();
    const interval = setInterval(calculateCountdown, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, [maintenanceInfo]);
  
  // Handle bypassing maintenance mode
  const handleBypassMaintenance = () => {
    localStorage.setItem('bypassMaintenance', 'true');
    setBypassMaintenance(true);
  };
  
  // Handle disabling bypass
  const handleDisableBypass = () => {
    localStorage.removeItem('bypassMaintenance');
    setBypassMaintenance(false);
  };
  
  if (loading) {
    console.log('MaintenanceBanner: Still loading');
    return null;
  }
  
  console.log('MaintenanceBanner: Loaded, maintenance info:', maintenanceInfo);
  console.log('MaintenanceBanner: User is admin:', isAdmin);
  console.log('MaintenanceBanner: Bypass maintenance:', bypassMaintenance);
  
  // If system is in maintenance mode and admin has not bypassed it, redirect to maintenance page
  if (maintenanceInfo?.maintenanceMode && !bypassMaintenance) {
    console.log('MaintenanceBanner: Redirecting to maintenance page');
    return <Navigate to="/maintenance" />;
  }
  
  // If no scheduled maintenance, don't show anything
  if (!maintenanceInfo?.scheduledMaintenanceDate) {
    console.log('MaintenanceBanner: No scheduled maintenance');
    return null;
  }
  
  // Check if scheduled maintenance date is in the past
  const now = new Date();
  
  // Handle different timestamp formats
  let maintenanceDate;
  if (maintenanceInfo.scheduledMaintenanceDate instanceof Timestamp) {
    maintenanceDate = maintenanceInfo.scheduledMaintenanceDate.toDate();
  } else if (maintenanceInfo.scheduledMaintenanceDate?.seconds) {
    // Handle serialized Timestamp objects
    maintenanceDate = new Date(maintenanceInfo.scheduledMaintenanceDate.seconds * 1000);
  } else {
    // Fallback for string dates
    maintenanceDate = new Date(maintenanceInfo.scheduledMaintenanceDate);
  }
  
  console.log('MaintenanceBanner: Current time:', now);
  console.log('MaintenanceBanner: Scheduled maintenance time:', maintenanceDate);
  
  if (now >= maintenanceDate) {
    // If current time is past the scheduled maintenance time but system is not in maintenance mode yet
    console.log('MaintenanceBanner: Maintenance time has passed, showing warning');
    return (
      <Banner status="warning" title="Maintenance Starting Soon">
        <p>System maintenance is scheduled to begin shortly. Please save your work and check back later.</p>
      </Banner>
    );
  }
  
  // Format the maintenance date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    // Handle different timestamp formats
    let date;
    if (timestamp instanceof Timestamp) {
      date = timestamp.toDate();
    } else if (timestamp?.seconds) {
      // Handle serialized Timestamp objects
      date = new Date(timestamp.seconds * 1000);
    } else {
      // Fallback for string dates
      date = new Date(timestamp);
    }
    
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Calculate estimated duration if end date is provided
  const getDurationText = () => {
    if (!maintenanceInfo.scheduledMaintenanceEndDate) return '';
    
    // Handle different timestamp formats for start date
    let startDate;
    if (maintenanceInfo.scheduledMaintenanceDate instanceof Timestamp) {
      startDate = maintenanceInfo.scheduledMaintenanceDate.toDate();
    } else if (maintenanceInfo.scheduledMaintenanceDate?.seconds) {
      startDate = new Date(maintenanceInfo.scheduledMaintenanceDate.seconds * 1000);
    } else {
      startDate = new Date(maintenanceInfo.scheduledMaintenanceDate);
    }
    
    // Handle different timestamp formats for end date
    let endDate;
    if (maintenanceInfo.scheduledMaintenanceEndDate instanceof Timestamp) {
      endDate = maintenanceInfo.scheduledMaintenanceEndDate.toDate();
    } else if (maintenanceInfo.scheduledMaintenanceEndDate?.seconds) {
      endDate = new Date(maintenanceInfo.scheduledMaintenanceEndDate.seconds * 1000);
    } else {
      endDate = new Date(maintenanceInfo.scheduledMaintenanceEndDate);
    }
    
    const durationMs = endDate - startDate;
    
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    let durationText = '';
    if (hours > 0) durationText += `${hours} hour${hours !== 1 ? 's' : ''} `;
    if (minutes > 0) durationText += `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    
    return durationText ? ` (Estimated duration: ${durationText})` : '';
  };
  
  return (
    <Banner status="info" title={maintenanceInfo.maintenanceTitle || 'Scheduled Maintenance'}>
      <Stack vertical spacing="tight">
        <Text>
          Maintenance scheduled for {formatDate(maintenanceInfo.scheduledMaintenanceDate)}
          {getDurationText()}
        </Text>
        {maintenanceInfo.maintenanceMessage && (
          <Text>{maintenanceInfo.maintenanceMessage}</Text>
        )}
        {countdown && (
          <div style={{ marginTop: '8px' }}>
            <Text>Maintenance begins in: {countdown.days > 0 ? `${countdown.days}d ` : ''}{countdown.hours}h {countdown.minutes}m</Text>
            <div style={{ marginTop: '4px' }}>
              <ProgressBar progress={100 - (countdown.percentage)} size="small" />
            </div>
          </div>
        )}
        {isAdmin && (
          <div style={{ marginTop: '8px' }}>
            {bypassMaintenance ? (
              <Button onClick={handleDisableBypass}>Disable Bypass</Button>
            ) : (
              <Button onClick={handleBypassMaintenance}>Bypass Maintenance</Button>
            )}
          </div>
        )}
      </Stack>
    </Banner>
  );
};

export default MaintenanceBanner;
